import { ref, computed, inject } from "vue";
import {
  fromRange,
  useCartesianScaler,
  useLinearScaler,
  type DOMState,
  type NumericDomain,
  type Value,
  type ValueFormatter
} from "@incytestudios/ddv";

import { decimalFormatter, formatNumber, roundFloat } from "@/utils/utils";

import { computePumpLabel } from "@/models/equipment/pumps/properties";

import {
  calculateBendingStressPoints,
  CASING_ROUGHNESS,
  TRAVERSE_CASING_ROUGHNESS,
  TUBING_JOINT_LENGTH_FT,
  type StressStation
} from "@/libs/calculations/bendingStress";
import { useWellboreGeometry } from "@/composables/wellboreGeometry/useWellboreGeometry";

import type { Scenario } from "@/composables/forms/formTypes";
import type {
  DeviationSurveyEntryRows,
  ScenarioCalculations
} from "@/composables/scenarioCalculations/calculationTypes";
import type { LengthItem, PumpEquipmentLengthItem, DatasetExtra } from "./d";

/** One chart per workbook sheet: `Final_Landing` and `Final_Traverse`. */
export const BENDING_STRESS_PLACEMENTS = ["landing", "traverse"] as const;

export type BendingStressPlacement = (typeof BENDING_STRESS_PLACEMENTS)[number];

export const BENDING_STRESS_PLACEMENT_TITLES: Record<
  BendingStressPlacement,
  string
> = {
  landing: "Final Landing",
  traverse: "Final Traverse"
};

export function getEquipmentMarkerStringLengthFt(
  startingStringLengthFt: number
): number {
  return startingStringLengthFt + TUBING_JOINT_LENGTH_FT;
}

export function useDeviationBendingStressChartData(props: {
  scenarioCalculations: ScenarioCalculations;
  scenario?: Scenario | undefined | null;
  entryRows: () => DeviationSurveyEntryRows["data"];
  /** Defaults to the landing. */
  placement?: () => BendingStressPlacement;
}) {
  const { scenarioCalculations, scenario } = props;
  const ddvTickFrequencies = inject<{ x: string; y: string }>(
    "ddvTickFrequencies"
  );

  const stepSizeFt = ref(0.1);

  /**
   * Rows 34..1957 of `Final_Landing` or `Final_Traverse`. The model spans the
   * equipment string plus one tubing joint above it, so `distanceAlongStringFt`
   * is 0 at the top of that joint, not at the top of the equipment.
   *
   * The traverse changes `C5`, `F4` and `I4`. The tubing and the casing
   * fallback stay at the landing depth, because the modelled joint is the same
   * bottom joint in both placements.
   */
  const dataValues = computed<StressStation[]>(() => {
    if (!equipmentDepthsFt.value.length) return [];
    const landingTubingLengthFt =
      scenarioCalculations.equipmentLengths.tubingLengthFt.value;
    const tubing = tubingAt(landingTubingLengthFt);
    const casing = casingAt(landingTubingLengthFt);
    if (!tubing || !casing) return [];
    const isTraverse = props.placement?.() === "traverse";
    const tubingLengthFt = isTraverse
      ? traverseTubingLengthFt.value
      : landingTubingLengthFt;
    if (tubingLengthFt === null) return [];
    const landingTubingWeightLb =
      scenarioCalculations.equipmentWeights.tubingWeightLbs.value;
    return calculateBendingStressPoints({
      tubingLengthFt,
      tubingCouplingOuterDiameterInches:
        tubing.coupling_outer_diameter_inches ?? 0,
      tubingOuterDiameterInches: tubing.outer_diameter_inches ?? 0,
      tubingInnerDiameterInches: tubing.interior_diameter_inches ?? 0,
      tubingPoundPerFoot: tubing.pound_per_foot ?? 0,
      equipment: equipmentDepthsFt.value,
      casingInnerDiameterAt: measuredDepthFt =>
        casingAt(measuredDepthFt)?.interior_diameter_inches ??
        casing.interior_diameter_inches ??
        0,
      // F4 = C5 * lb/ft. Scaling keeps the landing's mix of tubing sizes.
      tubingStringWeightLb: isTraverse
        ? (landingTubingWeightLb * tubingLengthFt) / landingTubingLengthFt
        : landingTubingWeightLb,
      fluidSpecificGravity:
        scenarioCalculations.pumpCalculations?.aggregate
          ?.averageLiquidSpecificGravity?.value,
      casingRoughness: isTraverse
        ? TRAVERSE_CASING_ROUGHNESS
        : CASING_ROUGHNESS,
      surveyAt,
      averageDls: props.scenario?.well_properties?.average_dls,
      stepSizeFt: stepSizeFt.value
    });
  });

  const domState = ref<DOMState>();
  const refreshingText = ref(false);

  const {
    equipmentSegments,
    traverseTubingLengthFt,
    surveyAt,
    casingAt,
    tubingAt
  } = useWellboreGeometry({
    scenarioCalculations,
    scenario,
    surveyRows: props.entryRows
  });

  const equipmentDepthsFt = equipmentSegments;

  const bendingStressDataset = computed<DatasetExtra>(() => {
    const points = dataValues.value.map(e => {
      return {
        x: e.distanceAlongStringFt,
        y: e.totalBendingStressPsi
      };
    });
    return {
      title: "Bending Stress",
      yAxisLabel: "Bending Stress (psi)",
      xAxisLabel: "Equipment Length (ft)",
      xDomain: equipmentStringLengthDomain.value,
      points,
      xValueFormatter: formatEquipmentLengthPoint,
      yValueFormatter: (v: Value, originalIndex: number) => {
        if (originalIndex === undefined || originalIndex === null) {
          return `${formatNumber(v as number)}`;
        } else {
          return `${formatNumber((dataValues.value[originalIndex]?.totalBendingStressPsi as number) || 0, 0)}`;
        }
      }
    } as DatasetExtra;
  });

  const deflectionDataset = computed<DatasetExtra>(() => {
    const points = dataValues.value.map(e => {
      return {
        x: e.distanceAlongStringFt,
        y: e.deflectionInches
      };
    });
    return {
      title: "Deflection",
      yAxisLabel: "Deflection (in)",
      xAxisLabel: "Equipment Length (ft)",
      xDomain: equipmentStringLengthDomain.value,
      points,
      xValueFormatter: formatEquipmentLengthPoint,
      yValueFormatter: (v: Value, originalIndex: number) => {
        if (originalIndex === undefined || originalIndex === null) {
          return `${formatNumber(v as number, 6)}`;
        } else {
          return `${formatNumber(dataValues.value[originalIndex].deflectionInches as number, 6)}`;
        }
      }
    } as DatasetExtra;
  });

  /** `AK` - the limit the stress curve must stay under. */
  const bendingStressLimitDataset = computed<DatasetExtra>(() => {
    const points = dataValues.value.map(e => {
      return {
        x: e.distanceAlongStringFt,
        y: e.bendingStressLimitPsi
      };
    });
    return {
      title: "Bending Stress Limit",
      yAxisLabel: "Bending Stress (psi)",
      xAxisLabel: "Equipment Length (ft)",
      xDomain: equipmentStringLengthDomain.value,
      points,
      xValueFormatter: formatEquipmentLengthPoint,
      yValueFormatter: (v: Value) => `${formatNumber(v as number, 0)}`
    } as DatasetExtra;
  });

  const deflectionDomain = computed<NumericDomain>(() => {
    const values = dataValues.value.map(p => p.deflectionInches);
    let max = Math.max(...(values.length ? values : [0]));
    let min = Math.min(...(values.length ? values : [0]));
    const diff = (max - min) * 0.1;
    max = max + diff;
    min = min - diff;
    if (max === min) {
      max++;
    }
    return {
      min,
      max
    };
  });

  // The DDV default tick formatter keeps 2 decimals, so a small deflection
  // shows as "0" or "-0". Below a span of 1, keep 2 decimals past the first
  // significant digit of the span (20 at most, the Intl limit). formatNumber
  // rounds the float noise of a tick, and adding 0 turns its -0 into 0.
  const deflectionTickFormatter = computed(() => {
    const span = deflectionDomain.value.max - deflectionDomain.value.min;
    const decimals =
      span > 0 && span < 1 ? Math.min(20, Math.ceil(-Math.log10(span)) + 2) : 2;
    return (value: Value) =>
      decimalFormatter(
        formatNumber(value as number, decimals) + 0,
        true,
        decimals
      );
  });

  const bendingStressDomain = computed<NumericDomain>(() => {
    const values = dataValues.value.flatMap(v => [
      v.totalBendingStressPsi,
      v.bendingStressLimitPsi
    ]);
    let max = Math.max(...(values.length ? values : [0]));
    let min = Math.min(...(values.length ? values : [0]));
    const diff = (max - min) * 0.1;
    max = max + diff;
    min = min - diff;
    if (max === min) {
      max++;
    }
    return {
      min,
      max
    };
  });

  const equipmentStringLengthDomain = computed<NumericDomain>(() => {
    return {
      min: 0,
      max:
        dataValues.value[dataValues.value.length - 1]?.distanceAlongStringFt ||
        1
    };
  });
  const datasets = computed<DatasetExtra[]>(() => {
    return [
      {
        ...deflectionDataset.value,
        domain: deflectionDomain.value,
        screenPoints: deflectionDatasetScreenPoints.value,
        scaler: deflectionLinearScaler
      },
      {
        ...bendingStressDataset.value,
        domain: bendingStressDomain.value,
        screenPoints: bendingStressDatasetScreenPoints.value,
        scaler: bendingStressLinearScaler
      },
      {
        ...bendingStressLimitDataset.value,
        domain: bendingStressDomain.value,
        screenPoints: bendingStressLimitDatasetScreenPoints.value,
        scaler: bendingStressLinearScaler
      }
    ];
  });

  const maxHeight = computed(() => domState.value?.plotHeight || 340);
  const maxWidth = computed(() => domState.value?.plotWidth);

  const targetDomainX = computed(() =>
    fromRange(domState.value?.plotWidth || 500)
  );
  const targetDomainY = computed(() =>
    fromRange(domState.value?.plotHeight || 340)
  );

  const equipmentStringLengthScaler = useLinearScaler(
    equipmentStringLengthDomain,
    targetDomainX,
    false
  );

  const deflectionLinearScaler = useLinearScaler(
    deflectionDomain,
    targetDomainY,
    true
  );

  const deflectionScaler = useCartesianScaler(
    equipmentStringLengthScaler,
    deflectionLinearScaler
  );
  const bendingStressLinearScaler = useLinearScaler(
    bendingStressDomain,
    targetDomainY,
    true
  );

  const bendingStressScaler = useCartesianScaler(
    equipmentStringLengthScaler,
    bendingStressLinearScaler
  );

  function isEquipmentPumpLengthItem(
    equipment: LengthItem
  ): equipment is PumpEquipmentLengthItem {
    return (
      typeof equipment === "object" && equipment !== null && "pump" in equipment
    );
  }

  /**
   * `distanceAlongStringFt` is 0 one tubing joint above the top of the
   * equipment, so shift the equipment offsets down by that joint before
   * matching them.
   */
  const getEquipmentAtStringLength = (stringLength: number) => {
    return equipmentDepthsFt.value.find(
      e =>
        Math.abs(
          getEquipmentMarkerStringLengthFt(e.startingStringLengthFt) -
            stringLength
        ) <
        stepSizeFt.value * 1.1
    );
  };

  const formatEquipmentLengthPoint: ValueFormatter = (
    value: Value,
    index?: number
  ): string => {
    const roundedValue = roundFloat(value as number);
    if (index === undefined || index === null) {
      return `${roundedValue}`;
    }
    const equipment = getEquipmentAtStringLength(value as number);
    if (!equipment) {
      return `${roundedValue}`;
    }
    if (isEquipmentPumpLengthItem(equipment)) {
      return `${roundedValue} ${
        computePumpLabel(
          equipment.pump.r_pump,
          equipment.pump.use_high_strength_shaft
        ) ?? "(pump)"
      }`;
    } else {
      return `${roundedValue} ${equipment.xref.display_label || `(${equipment.type})`}`;
    }
  };

  const deflectionDatasetScreenPoints = computed(() => {
    return deflectionDataset.value.points.map(p => {
      return deflectionScaler.scale(p);
    });
  });
  const bendingStressDatasetScreenPoints = computed(() => {
    return bendingStressDataset.value.points.map(p => {
      return bendingStressScaler.scale(p);
    });
  });
  const bendingStressLimitDatasetScreenPoints = computed(() => {
    return bendingStressLimitDataset.value.points.map(p => {
      return bendingStressScaler.scale(p);
    });
  });

  return {
    deflectionDatasetScreenPoints,
    bendingStressDatasetScreenPoints,
    bendingStressLimitDatasetScreenPoints,
    domState,
    ddvTickFrequencies,
    refreshingText,
    equipmentDepthsFt,
    bendingStressDataset,
    bendingStressLimitDataset,
    deflectionDataset,
    deflectionDomain,
    deflectionTickFormatter,
    bendingStressDomain,
    equipmentStringLengthDomain,
    datasets,
    equipmentStringLengthScaler,
    deflectionScaler,
    bendingStressScaler,
    bendingStressLinearScaler,
    deflectionLinearScaler,
    maxHeight,
    maxWidth,
    targetDomainX,
    targetDomainY,
    isEquipmentPumpLengthItem,
    formatEquipmentLengthPoint
  };
}
